import React from "react";
import styled from "styled-components";
import { FiArrowUpRight, FiGithub } from "react-icons/fi";
import MonogramTile from "../Visual/MonogramTile";
import { Bio } from "../../data/constants";

const Card = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.035);
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.35s ease, border-color 0.35s ease, box-shadow 0.35s ease;

  &:hover {
    transform: translateY(-6px);
    border-color: rgba(139, 124, 255, 0.4);
    box-shadow: 0 24px 60px rgba(0, 0, 0, 0.35);
  }
`;

const Visual = styled.div`
  height: ${({ $featured }) => ($featured ? "200px" : "160px")};
  position: relative;
`;

const Content = styled.div`
  padding: 20px 22px 22px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex: 1;
`;

const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${({ theme }) => theme.secondary};
  background: rgba(34, 211, 238, 0.1);
  border: 1px solid rgba(34, 211, 238, 0.2);
  padding: 3px 9px;
  border-radius: 100px;
`;

const Title = styled.h3`
  font-family: "Space Grotesk", sans-serif;
  font-size: ${({ $featured }) => ($featured ? "19px" : "17px")};
  font-weight: 600;
  color: ${({ theme }) => theme.text_primary};
  line-height: 1.35;
`;

const Date = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.text_muted};
`;

const Description = styled.p`
  font-size: 13.5px;
  line-height: 1.65;
  color: ${({ theme }) => theme.text_secondary};
  display: -webkit-box;
  -webkit-line-clamp: ${({ $featured }) => ($featured ? 4 : 3)};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const Footer = styled.div`
  margin-top: auto;
  padding-top: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ViewLink = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.primary};
`;

const IconLinks = styled.div`
  display: flex;
  gap: 8px;
`;

const IconAnchor = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  color: ${({ theme }) => theme.text_secondary};
  border: 1px solid rgba(255, 255, 255, 0.09);
  transition: all 0.2s ease;

  &:hover {
    color: ${({ theme }) => theme.text_primary};
    border-color: ${({ theme }) => theme.primary};
  }
`;

const ProjectCard = ({ project, setOpenModal, featured }) => {
  const hasLiveApp = project.webapp && project.webapp !== Bio.github;
  const hasCode = project.github && project.github !== Bio.github;

  return (
    <Card onClick={() => setOpenModal({ state: true, project })}>
      <Visual $featured={featured}>
        <MonogramTile label={project.title} seed={project.title} minHeight="100%" />
      </Visual>
      <Content>
        <Tags>
          {project.tags?.slice(0, featured ? 6 : 3).map((tag) => (
            <Tag key={tag}>{tag}</Tag>
          ))}
        </Tags>
        <Title $featured={featured}>{project.title}</Title>
        <Date>{project.date}</Date>
        <Description $featured={featured}>{project.description}</Description>
        <Footer>
          <ViewLink>
            View Details <FiArrowUpRight />
          </ViewLink>
          {(hasLiveApp || hasCode) && (
            <IconLinks onClick={(e) => e.stopPropagation()}>
              {hasCode && (
                <IconAnchor href={project.github} target="_blank" rel="noopener noreferrer" aria-label="Source code">
                  <FiGithub size={14} />
                </IconAnchor>
              )}
              {hasLiveApp && (
                <IconAnchor href={project.webapp} target="_blank" rel="noopener noreferrer" aria-label="Live app">
                  <FiArrowUpRight size={14} />
                </IconAnchor>
              )}
            </IconLinks>
          )}
        </Footer>
      </Content>
    </Card>
  );
};

export default ProjectCard;
